/**
 * Thread @-mention resolver — pure (group-threading P2b).
 *
 * Turns a sent message's text into the set of workspace-member uids to summon.
 * Resolves two forms against the account roster:
 *   1. `<@uid>` tokens (precise — inserted by the autocomplete).
 *   2. plain `@query` mentions (typed) — matched UNAMBIGUOUSLY against the roster in
 *      two tiers: an EXACT identifier (uid / email / email-local-part) first, and a
 *      whitespace-stripped displayName PREFIX only when nothing matched exactly.
 *
 * FAIL-CLOSED: a `@query` that matches zero OR more than one member is skipped (never
 * summon the wrong person). The server (`threadMembershipWrite` via P2a) stays the
 * sole authority — this only proposes uids. Pure + React-free.
 */
import { parseMentionUids } from './mentionParser';

export interface MentionRosterEntry {
  userId: string;
  displayName?: string;
  email?: string;
  /**
   * The profile's contact e-mail, when the member doc's own `email` is blank
   * or differs (2026-09-08). An exact match on EITHER address, or its
   * local-part, names the person precisely.
   */
  profileEmail?: string;
}

/** `@query` where `@` opens a word; query is name/email-ish chars (no spaces, no `@`). */
const PLAIN_MENTION = /(?:^|\s)@([A-Za-z0-9._%+-]+)/g;

function norm(v: string | undefined): string {
  return (v ?? '').trim().toLowerCase();
}

/**
 * Whitespace-stripped, lowercased name — so "QA Teammate" matches a "@qa" query.
 *
 * Punctuation the `@query` grammar cannot carry is also stripped (2026-09-08).
 * A plain mention is captured by PLAIN_MENTION, which accepts only
 * `[A-Za-z0-9._%+-]`, and `deriveMentionHandle` sanitizes its offered handles to
 * that same set — so it offered "obrien" for "O'Brien" while this comparison
 * still demanded the apostrophe, and the handle could never verify. Anyone with
 * an apostrophe or an accent in their name (O'Brien, D'Angelo, Muñoz) was
 * therefore unmentionable by any readable handle and fell through to a raw uid.
 * Sanitizing BOTH sides is what makes the two halves agree.
 */
const HANDLE_UNSAFE = /[^A-Za-z0-9._%+-]/g;
function nameKey(v: string | undefined): string {
  return norm(v).replace(/\s+/g, '').replace(HANDLE_UNSAFE, '');
}

function emailLocal(email: string | undefined): string {
  const e = norm(email);
  const at = e.indexOf('@');
  return at > 0 ? e.slice(0, at) : e;
}

/** Extract plain `@query` tokens (word-opening `@`), excluding `<@uid>` tokens. */
export function extractPlainMentionQueries(text: string): string[] {
  const out: string[] = [];
  if (!text) return out;
  PLAIN_MENTION.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PLAIN_MENTION.exec(text)) !== null) {
    const q = (m[1] ?? '').trim();
    if (q) out.push(q);
  }
  return out;
}

/**
 * Resolve a message's mentions to a de-duplicated list of roster uids to summon.
 * `<@uid>` tokens are kept only if they name a real roster member; plain `@query`
 * mentions resolve only when EXACTLY one member matches.
 */
export function resolveMentionsToUids(
  text: string,
  roster: ReadonlyArray<MentionRosterEntry>,
): string[] {
  const members = (roster ?? []).filter((m) => m && String(m.userId ?? '').trim());
  const byId = new Map<string, string>(); // uid → uid (identity, for membership check)
  for (const m of members) byId.set(String(m.userId).trim(), String(m.userId).trim());

  const result: string[] = [];
  const seen = new Set<string>();
  const add = (uid: string) => {
    const id = (uid ?? '').trim();
    if (id && byId.has(id) && !seen.has(id)) {
      seen.add(id);
      result.push(id);
    }
  };

  // 1. Precise <@uid> tokens (validated against the roster).
  for (const uid of parseMentionUids(text)) add(uid);

  // 2. Plain @query mentions — unambiguous match only, in TWO TIERS.
  //
  // TIERED PRECEDENCE (2026-09-08, Fernando-workspace report): an EXACT match on
  // an identifier the person owns (uid / email / email local-part) is a precise
  // reference to THEM. A displayName PREFIX match is a guess at who was meant.
  // Weighing both equally let one person's NAME make another person's OWN email
  // ambiguous: a member doc carrying a COLLEAGUE's stale name meant a query that
  // was one teammate's exact email local-part also prefix-matched the other's
  // name — two matches, fail-closed, no summon. And because deriveMentionHandle
  // only offers handles this resolver verifies, the composer fell through to the
  // raw uid and put a 28-character id in a client-visible thread.
  //
  // Exact first, prefix only when nothing matched exactly. Two real teammates
  // who genuinely share a name can no longer break each other's mentions.
  // Fail-closed is UNCHANGED and applies WITHIN each tier: a tier that matches
  // zero or several people yields no summon, and an ambiguous exact tier never
  // falls through to a prefix guess (an exact hit means the query named an
  // identifier — the answer is there or nowhere).
  for (const rawQuery of extractPlainMentionQueries(text)) {
    const q = norm(rawQuery);
    if (!q) continue;
    const exact = members.filter(
      (m) => norm(m.userId) === q
        || norm(m.email) === q || emailLocal(m.email) === q
        || (!!norm(m.profileEmail) && (norm(m.profileEmail) === q || emailLocal(m.profileEmail) === q)),
    );
    const matches = exact.length > 0
      ? exact
      : members.filter(
          (m) => nameKey(m.displayName).length > 0 && nameKey(m.displayName).startsWith(q),
        );
    if (matches.length === 1) add(String(matches[0].userId).trim());
    // 0 or >1 matches in the deciding tier ⇒ ambiguous ⇒ skip (fail-closed).
  }

  return result;
}
